import type { SlashCommand } from "~/lib/slash-commands";
import { MemberKindBadge } from "~/components/MemberKindBadge";

export interface SlashPaletteProps {
  open: boolean;
  commands: readonly SlashCommand[];
  activeIndex: number;
  textarea: HTMLTextAreaElement | null;
  onPick: (cmd: SlashCommand) => void;
}

export function SlashPalette({ open, commands, activeIndex, textarea, onPick }: SlashPaletteProps) {
  if (!open || !textarea) return null;
  return (
    <div
      role="listbox"
      aria-label="Slash commands"
      style={{
        position: "absolute",
        left: 0,
        bottom: "calc(100% + 4px)",
        width: 360,
        maxHeight: 400,
        overflowY: "auto",
        borderRadius: "var(--chat-radius-xl)",
        border: "1px solid var(--chat-surface-border)",
        background: "var(--chat-surface-overlay)",
        color: "var(--chat-fg-primary)",
        boxShadow: "var(--chat-shadow-popover)",
        backdropFilter: "blur(8px)",
        paddingBlock: "var(--chat-space-1)",
        zIndex: 30,
      }}
    >
      {commands.length === 0 ? (
        <div
          style={{
            padding: "var(--chat-space-3)",
            fontFamily: "var(--chat-font-sans)",
            fontSize: "var(--chat-type-body-sm-size)",
            lineHeight: "var(--chat-type-body-sm-line)",
            color: "var(--chat-fg-muted)",
          }}
        >
          No matching commands
        </div>
      ) : (
        commands.map((cmd, index) => {
          const active = index === activeIndex;
          const rowKey = cmd.agent ? `${cmd.agent.userId}:${cmd.name}` : cmd.name;
          return (
            <button
              key={rowKey}
              type="button"
              role="option"
              aria-selected={active}
              className="chat-button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => onPick(cmd)}
              style={{
                width: "100%",
                minHeight: 56,
                display: "block",
                border: "none",
                background: active ? "var(--chat-surface-raised)" : "transparent",
                color: "inherit",
                cursor: "pointer",
                padding: 0,
                textAlign: "left",
              }}
            >
              <span
                style={{
                  display: "grid",
                  gap: 1,
                  padding: "var(--chat-space-2) var(--chat-space-3)",
                }}
              >
                {cmd.agent && (
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                      fontFamily: "var(--chat-font-sans)",
                      fontSize: "var(--chat-type-body-xs-size)",
                      lineHeight: "var(--chat-type-body-xs-line)",
                      color: "var(--chat-fg-secondary)",
                    }}
                  >
                    <span>{cmd.agent.displayName}</span>
                    <MemberKindBadge userId={cmd.agent.userId} />
                  </span>
                )}
                <span
                  style={{
                    fontFamily: "var(--chat-font-sans)",
                    fontSize: "var(--chat-type-body-md-size)",
                    lineHeight: "var(--chat-type-body-md-line)",
                    fontWeight: 500,
                  }}
                >
                  /{cmd.name}
                </span>
                <span
                  style={{
                    fontFamily: "var(--chat-font-sans)",
                    fontSize: "var(--chat-type-body-sm-size)",
                    lineHeight: "var(--chat-type-body-sm-line)",
                    color: "var(--chat-fg-secondary)",
                  }}
                >
                  {cmd.description}
                </span>
                <span
                  style={{
                    fontFamily: "var(--chat-font-mono)",
                    fontSize: "var(--chat-type-mono-size)",
                    lineHeight: "var(--chat-type-mono-line)",
                    color: "var(--chat-fg-muted)",
                  }}
                >
                  {cmd.usage}
                </span>
              </span>
            </button>
          );
        })
      )}
    </div>
  );
}
